/**
 * `<EmptyState />` — Story 5.4 (UX §4.1 line 888 + UX §4.4, AC line 1015).
 *
 * Centered illustration + copy + optional action CTA. Consumed by
 * Story 8.6 submitter empty queue, Story 9.7 voter empty gallery,
 * Story 7.x admin "create your first event" prompt, etc.
 *
 * Slots:
 *   - `illustration?` — consumer-provided SVG / icon. Defaults to a
 *                       neutral 64×64 outlined-circle SVG.
 *   - `children`      — copy (required).
 *   - `action?`       — optional CTA (typically `<Button />`).
 *
 * Layout: vertical flex; centered; `--space-4` gap; `--space-7` vertical
 * padding (UX §3.1 line 267 — "centered illustration").
 */
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  illustration?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}

const DEFAULT_ICON_SIZE = 64;

function DefaultIllustration() {
  return (
    <svg
      width={DEFAULT_ICON_SIZE}
      height={DEFAULT_ICON_SIZE}
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
      data-testid="empty-state-default-illustration"
      style={{ color: 'var(--text-muted)' }}
    >
      <circle cx="32" cy="32" r="28" />
    </svg>
  );
}

/**
 * `EmptyState` — centered "no content" panel.
 */
export function EmptyState({ illustration, children, action }: EmptyStateProps) {
  return (
    <div
      data-testid="empty-state"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-7) var(--space-4)',
        textAlign: 'center',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {illustration ?? <DefaultIllustration />}
      <div data-testid="empty-state-copy">{children}</div>
      {action && <div data-testid="empty-state-action">{action}</div>}
    </div>
  );
}
